// src/pages/CreateRequirement.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { requirementApi } from '../../feddback-services/api';
import './CreateRequirement.css';

const CreateRequirement = () => {
  const navigate = useNavigate();
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [filters, setFilters] = useState({
    status: '',
    department: '',
    location: '',
    job_type: '',
    q: '',
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1,
  });

  // ✅ Experience Options
  const EXPERIENCE_OPTIONS = [
    { value: '0-0.5', label: '0-6 months' },
    { value: '0.5-1', label: '6 months - 1 year' },
    { value: '1-2', label: '1 - 2 years' },
    { value: '2-4', label: '2 - 4 years' },
    { value: '4-6', label: '4 - 6 years' },
    { value: '6-8', label: '6 - 8 years' },
    { value: '8-10', label: '8 - 10 years' },
    { value: '10-12', label: '10 - 12 years' },
    { value: '12-15', label: '12 - 15 years' },
    { value: '15+', label: '15+ years' },
  ];

  // Form state — locations as { name, is_open }
  const [formData, setFormData] = useState({
    requirement_code: '',
    title: '',
    job_description: '',
    department: '',
    locations: [],
    location: '',
    job_type: 'full-time',
    experience_range: '',
    experience_min: null,
    experience_max: null,
    status: 'open',
    fields: [],
  });

  // Location input state
  const [locationInput, setLocationInput] = useState('');

  // Field management state
  const [fieldInput, setFieldInput] = useState({
    field_name: '',
    field_type: 'text',
    is_required: false,
    options: [],
    optionInput: '',
  });
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);

  // Field types options
  const fieldTypes = [
    { value: 'text', label: 'Text Input', icon: 'fa-font' },
    { value: 'number', label: 'Number Input', icon: 'fa-hashtag' },
    { value: 'email', label: 'Email Input', icon: 'fa-envelope' },
    { value: 'date', label: 'Date Picker', icon: 'fa-calendar' },
    { value: 'file', label: 'File Upload', icon: 'fa-upload' },
    { value: 'textarea', label: 'Text Area', icon: 'fa-align-left' },
  ];

  // Check authentication and role on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('userRole');
    const name = localStorage.getItem('userName');

    console.log('🔐 Current User:', {
      token: token ? 'Present' : 'Missing',
      role: role,
      name: name,
    });

    setUserRole(role || '');
    setIsAuthenticated(!!token);

    if (!token) {
      showNotification('Please login to manage requirements', 'warning');
      navigate('/login');
      return;
    }

    if (!['admin', 'super_admin'].includes(role)) {
      showNotification(
        `You are logged in as "${role || 'Unknown'}". Admin or Super Admin access required to create requirements.`,
        'warning'
      );
    }
  }, []);

  // Auto-hide notification
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ show: false, message: '', type: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  // Load requirements
  useEffect(() => {
    if (isAuthenticated) {
      fetchRequirements();
    }
  }, [filters, pagination.page, isAuthenticated]);

  // ========== NOTIFICATION ==========
  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
  };

  // ========== HELPER — Safely read is_open OR enabled ==========
  const isLocOpen = (loc) => {
    if (loc.is_open !== undefined) return loc.is_open !== false;
    if (loc.enabled !== undefined) return loc.enabled !== false;
    return true;
  };

  // ========== API CALLS ==========
  const fetchRequirements = async () => {
    setLoading(true);
    try {
      const response = await requirementApi.list({
        ...filters,
        page: pagination.page,
        limit: pagination.limit,
      });
      const responseData = response.data.data || response.data;
      setRequirements(responseData.items || []);
      setPagination(responseData.pagination || pagination);
    } catch (error) {
      console.error('Error fetching requirements:', error);
      if (error.response?.status === 401) {
        showNotification('Please login to view requirements', 'error');
        navigate('/login');
      } else if (error.response?.status === 403) {
        showNotification("You don't have permission to view requirements", 'error');
      } else {
        showNotification('Failed to fetch requirements', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  // ========== LOCATION MANAGEMENT ==========
  const handleAddLocation = () => {
    if (!locationInput.trim()) return;

    const isDuplicate = formData.locations.some(
      (loc) => loc.name.toLowerCase() === locationInput.trim().toLowerCase()
    );
    if (isDuplicate) {
      showNotification('This location is already added', 'warning');
      return;
    }

    setFormData((prev) => ({
      ...prev,
      locations: [
        ...prev.locations,
        { name: locationInput.trim(), is_open: true }, // ✅ is_open
      ],
    }));

    setLocationInput('');
  };

  const handleRemoveLocation = (index) => {
    setFormData((prev) => ({
      ...prev,
      locations: prev.locations.filter((_, i) => i !== index),
    }));
  };

  const handleLocationKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddLocation();
    }
  };

  // ✅ Toggle enable/disable
  const handleToggleLocation = (index) => {
    setFormData((prev) => ({
      ...prev,
      locations: prev.locations.map((loc, i) =>
        i === index ? { ...loc, is_open: !isLocOpen(loc) } : loc
      ),
    }));
  };

  // ✅ Toggle all
  const handleToggleAll = () => {
    if (formData.locations.length === 0) return;
    const allOn = formData.locations.every((l) => isLocOpen(l));
    setFormData((prev) => ({
      ...prev,
      locations: prev.locations.map((l) => ({
        ...l,
        is_open: !allOn,
      })),
    }));
  };

  // ========== FORM HANDLERS ==========
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'experience_range' && value) {
      const parts = value.split('-');
      if (parts.length === 2) {
        const min = parseFloat(parts[0]);
        const max = parts[1] === '+' ? null : parseFloat(parts[1]);
        setFormData((prev) => ({
          ...prev,
          experience_min: min,
          experience_max: max,
        }));
      }
    } else if (name === 'experience_range' && !value) {
      setFormData((prev) => ({
        ...prev,
        experience_min: null,
        experience_max: null,
      }));
    }
  };

  // ========== FIELD MANAGEMENT ==========
  const handleFieldInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFieldInput((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleAddField = () => {
    if (!fieldInput.field_name.trim()) {
      showNotification('Please enter a field name', 'warning');
      return;
    }

    const newField = {
      field_name: fieldInput.field_name.trim(),
      field_key: slugify(fieldInput.field_name.trim()),
      field_type: fieldInput.field_type,
      is_required: fieldInput.is_required,
      options: [],
      sort_order: formData.fields.length,
    };

    if (editingFieldIndex !== null) {
      const updatedFields = [...formData.fields];
      updatedFields[editingFieldIndex] = newField;
      setFormData((prev) => ({ ...prev, fields: updatedFields }));
      setEditingFieldIndex(null);
      showNotification('Field updated successfully', 'success');
    } else {
      setFormData((prev) => ({
        ...prev,
        fields: [...prev.fields, newField],
      }));
      showNotification('Field added successfully', 'success');
    }

    setFieldInput({
      field_name: '',
      field_type: 'text',
      is_required: false,
      options: [],
      optionInput: '',
    });
  };

  const handleEditField = (index) => {
    const field = formData.fields[index];
    setFieldInput({
      field_name: field.field_name,
      field_type: field.field_type,
      is_required: field.is_required || false,
      options: field.options || [],
      optionInput: '',
    });
    setEditingFieldIndex(index);
  };

  const handleRemoveField = (index) => {
    const updatedFields = formData.fields.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, fields: updatedFields }));
    showNotification('Field removed', 'info');
  };

  // ========== MAIN FORM SUBMIT ==========
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAuthenticated) {
      showNotification('Please login to create requirements', 'error');
      navigate('/login');
      return;
    }

    if (!formData.requirement_code) {
      showNotification('Requirement code is required', 'error');
      return;
    }
    if (!formData.title) {
      showNotification('Title is required', 'error');
      return;
    }

    setSubmitting(true);
    try {
      // ✅ Send { name, is_open } — backend schema supports is_open
      const payload = {
        ...formData,
        locations: formData.locations.map((loc) => ({
          name: loc.name,
          is_open: isLocOpen(loc), // ✅ is_open directly
        })),
        experience_min: formData.experience_min || null,
        experience_max: formData.experience_max || null,
      };

      if (editingId) {
        await requirementApi.update(editingId, payload);
        showNotification('Requirement updated successfully', 'success');
      } else {
        await requirementApi.create(payload);
        showNotification('Requirement created successfully with status: OPEN', 'success');
      }

      await fetchRequirements();
      resetForm();
    } catch (error) {
      console.error('Error saving requirement:', error);
      console.error('Response data:', error.response?.data);
      console.error('Status:', error.response?.status);

      if (error.response?.status === 403) {
        const role = localStorage.getItem('userRole');
        showNotification(
          `Permission denied! You are logged in as "${role || 'Unknown'}". Only Admin and Super Admin can create requirements. Please login with Admin credentials.`,
          'error'
        );
      } else if (error.response?.status === 401) {
        showNotification('Authentication required. Please login.', 'error');
        navigate('/login');
      } else if (error.response?.data?.message) {
        showNotification(error.response.data.message, 'error');
      } else {
        showNotification('Failed to save requirement', 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ========== JD FILE UPLOAD ==========
  const handleFileUpload = async (id, file) => {
    if (!file) return;

    if (!isAuthenticated) {
      showNotification('Please login to upload files', 'error');
      navigate('/login');
      return;
    }

    try {
      await requirementApi.uploadJd(id, file);
      showNotification('JD file uploaded successfully', 'success');
      await fetchRequirements();
    } catch (error) {
      console.error('Error uploading JD:', error);
      if (error.response?.status === 401) {
        showNotification('Authentication required. Please login.', 'error');
        navigate('/login');
      } else {
        showNotification('Failed to upload JD file', 'error');
      }
    }
  };

  // ========== EDIT ==========
  const handleEdit = async (id) => {
    if (!isAuthenticated) {
      showNotification('Please login to edit requirements', 'error');
      navigate('/login');
      return;
    }

    try {
      setLoading(true);
      const response = await requirementApi.get(id);
      const data = response.data.data || response.data;

      // ✅ Normalize — backend deto is_open (ani stats pan)
      let locations = data.locations || [];
      if (Array.isArray(locations)) {
        locations = locations
          .map((loc) => {
            if (typeof loc === 'string') {
              return { name: loc, is_open: true };
            }
            if (typeof loc === 'object' && loc !== null) {
              return {
                name: loc.name || loc.location || loc.city || '',
                is_open:
                  loc.is_open !== undefined
                    ? loc.is_open !== false
                    : loc.enabled !== undefined
                    ? loc.enabled !== false
                    : true,
              };
            }
            return { name: String(loc), is_open: true };
          })
          .filter((loc) => loc.name);
      }

      // Experience range from min/max
      let experience_range = '';
      if (data.experience_min !== null && data.experience_min !== undefined) {
        if (data.experience_max === null) {
          experience_range = `${data.experience_min}+`;
        } else {
          experience_range = `${data.experience_min}-${data.experience_max}`;
        }
      }

      setFormData({
        requirement_code: data.requirement_code || '',
        title: data.title || '',
        job_description: data.job_description || '',
        department: data.department || '',
        locations: locations,
        location: '',
        job_type: data.job_type || 'full-time',
        experience_range: experience_range,
        experience_min: data.experience_min || null,
        experience_max: data.experience_max || null,
        status: data.status || 'open',
        fields: data.fields || [],
      });
      setEditingId(id);
      setShowForm(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error fetching requirement:', error);
      if (error.response?.status === 401) {
        showNotification('Authentication required. Please login.', 'error');
        navigate('/login');
      } else {
        showNotification('Failed to fetch requirement details', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // ========== DELETE ==========
  const handleDelete = async (id) => {
    if (!isAuthenticated) {
      showNotification('Please login to delete requirements', 'error');
      navigate('/login');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this requirement?')) return;

    try {
      await requirementApi.delete(id);
      showNotification('Requirement deleted successfully', 'success');
      await fetchRequirements();
    } catch (error) {
      console.error('Error deleting requirement:', error);
      if (error.response?.status === 401) {
        showNotification('Authentication required. Please login.', 'error');
        navigate('/login');
      } else {
        showNotification('Failed to delete requirement', 'error');
      }
    }
  };

  // ========== RESET FORM ==========
  const resetForm = () => {
    setFormData({
      requirement_code: '',
      title: '',
      job_description: '',
      department: '',
      locations: [],
      location: '',
      job_type: 'full-time',
      experience_range: '',
      experience_min: null,
      experience_max: null,
      status: 'open',
      fields: [],
    });
    setLocationInput('');
    setFieldInput({
      field_name: '',
      field_type: 'text',
      is_required: false,
      options: [],
      optionInput: '',
    });
    setEditingId(null);
    setEditingFieldIndex(null);
    setShowForm(false);
  };

  // ========== UTILITY ==========
  const slugify = (text) => {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^\w-]/g, '')
      .replace(/_+/g, '_');
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      draft: { class: 'status-draft', label: 'Draft' },
      open: { class: 'status-open', label: 'Open' },
      closed: { class: 'status-closed', label: 'Closed' },
    };
    const s = statusMap[status] || statusMap.open;
    return <span className={`req-status-badge ${s.class}`}>{s.label}</span>;
  };

  const getFieldTypeIcon = (type) => {
    const icons = {
      text: 'fa-font',
      number: 'fa-hashtag',
      email: 'fa-envelope',
      phone: 'fa-phone',
      date: 'fa-calendar',
      dropdown: 'fa-caret-down',
      textarea: 'fa-align-left',
      file: 'fa-file',
      boolean: 'fa-check-square',
      checkbox: 'fa-check-square',
      radio: 'fa-dot-circle',
      select: 'fa-caret-down',
    };
    return icons[type] || 'fa-cog';
  };

  const getFieldTypeLabel = (type) => {
    const labels = {
      text: 'Text Input',
      number: 'Number Input',
      email: 'Email Input',
      date: 'Date Picker',
      file: 'File Upload',
      checkbox: 'Checkbox',
      radio: 'Radio Button',
      textarea: 'Text Area',
      select: 'Dropdown Select',
    };
    return labels[type] || type;
  };

  const getNotificationClass = () => {
    switch (notification.type) {
      case 'success':
        return 'notification-success';
      case 'error':
        return 'notification-error';
      case 'warning':
        return 'notification-warning';
      case 'info':
        return 'notification-info';
      default:
        return 'notification-success';
    }
  };

  const hasPermission = isAuthenticated && ['admin', 'super_admin'].includes(userRole);

  // Access denied
  if (!hasPermission && isAuthenticated) {
    return (
      <div className="access-denied-container">
        <div className="access-denied-card">
          <i className="fas fa-lock"></i>
          <h2>Access Denied</h2>
          <p>You don't have permission to create requirements.</p>
          <p className="role-info">
            Your role: <strong>{userRole || 'Unknown'}</strong>
          </p>
          <p className="required-role">Required: Admin or Super Admin</p>
          <button
            className="btn-go-back"
            onClick={() => navigate('/admin/view-requirement')}
          >
            <i className="fas fa-arrow-left"></i> Go Back
          </button>
        </div>
      </div>
    );
  }

  // ========== RENDER ==========
  return (
    <div className="requirement-container">
      {/* Notification */}
      {notification.show && (
        <div className={`notification ${getNotificationClass()}`}>
          <span className="notification-message">{notification.message}</span>
          <button
            className="notification-close"
            onClick={() => setNotification({ show: false, message: '', type: '' })}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      {/* Auth Warning */}
      {!isAuthenticated && (
        <div className="auth-warning-banner">
          <i className="fas fa-lock"></i>
          You are viewing requirements in read-only mode.
          <button className="btn-login" onClick={() => navigate('/login')}>
            Login to manage
          </button>
        </div>
      )}

      <div className="requirement-header">
        <h1>
          <i className="fas fa-bullhorn"></i> Manage Requirements
        </h1>
        <button
          className="btn-create-requirement"
          onClick={() => setShowForm(!showForm)}
          disabled={loading || !hasPermission}
        >
          <i className={`fas ${showForm ? 'fa-times' : 'fa-plus'}`}></i>
          {showForm ? 'Close Form' : 'New Requirement'}
        </button>
      </div>

      {/* FILTERS */}
      <div className="filter-bar">
        <div className="filter-group">
          <input
            type="text"
            name="q"
            value={filters.q}
            onChange={handleFilterChange}
            placeholder="Search by title or code..."
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            className="filter-select"
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div className="filter-group">
          <input
            type="text"
            name="department"
            value={filters.department}
            onChange={handleFilterChange}
            placeholder="Department..."
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <input
            type="text"
            name="location"
            value={filters.location}
            onChange={handleFilterChange}
            placeholder="Location..."
            className="filter-input"
          />
        </div>
        <button className="btn-refresh" onClick={fetchRequirements} disabled={loading}>
          <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
        </button>
      </div>

      {/* FORM */}
      {showForm && (
        <div className="requirement-form-card">
          <form onSubmit={handleSubmit} className="requirement-form">
            <h2>{editingId ? 'Edit Requirement' : 'Create New Requirement'}</h2>

            {/* Basic Info */}
            <div className="form-row">
              <div className="form-group">
                <label>
                  Requirement Code <span className="required-star">*</span>
                </label>
                <input
                  type="text"
                  name="requirement_code"
                  value={formData.requirement_code}
                  onChange={handleInputChange}
                  placeholder="e.g., REQ-2024-001"
                  className="form-input"
                  required
                  disabled={!!editingId}
                />
              </div>

              <div className="form-group">
                <label>
                  Title <span className="required-star">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Senior Software Engineer"
                  className="form-input"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Department</label>
                <input
                  type="text"
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  placeholder="e.g., Engineering"
                  className="form-input"
                />
              </div>
            </div>

            {/* ✅ COMPACT LOCATIONS */}
            <div className="form-group full-width">
              <label>
                <i className="fas fa-map-marker-alt"></i> Locations
                {formData.locations.length > 0 && (
                  <>
                    <span className="location-count-badge">
                      {formData.locations.filter((l) => isLocOpen(l)).length} active /{' '}
                      {formData.locations.length} total
                    </span>
                    <button
                      type="button"
                      className="btn-bulk-toggle"
                      onClick={handleToggleAll}
                    >
                      {formData.locations.every((l) => isLocOpen(l))
                        ? 'Disable All'
                        : 'Enable All'}
                    </button>
                  </>
                )}
              </label>

              {/* Add Location Row */}
              <div className="location-add-row">
                <input
                  type="text"
                  value={locationInput}
                  onChange={(e) => setLocationInput(e.target.value)}
                  onKeyDown={handleLocationKeyDown}
                  placeholder="Type location and press Enter (e.g., Pune)"
                  className="form-input location-name-input"
                />
                <button
                  type="button"
                  onClick={handleAddLocation}
                  className="btn-add-location"
                >
                  <i className="fas fa-plus"></i> Add
                </button>
              </div>

              <small className="form-hint">
                <i className="fas fa-info-circle"></i> Click on any location chip to
                toggle ON/OFF. Click ✕ to remove.
              </small>

              {/* Compact Chips */}
              {formData.locations.length > 0 && (
                <div className="location-chips-wrap">
                  {formData.locations.map((loc, idx) => {
                    const open = isLocOpen(loc);
                    return (
                      <div
                        key={idx}
                        className={`location-chip-item ${open ? 'chip-on' : 'chip-off'}`}
                        onClick={() => handleToggleLocation(idx)}
                        title={open ? 'Click to disable' : 'Click to enable'}
                      >
                        <span className={`chip-dot ${open ? 'dot-on' : 'dot-off'}`}></span>
                        <span className="chip-name">{loc.name}</span>
                        {/* <button
                          type="button"
                          className="chip-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveLocation(idx);
                          }}
                          title="Remove location"
                        >
                          <i className="fas fa-times"></i>
                        </button> */}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Job Type</label>
                <select
                  name="job_type"
                  value={formData.job_type}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="full-time">Full Time</option>
                  <option value="part-time">Part Time</option>
                  <option value="contract">Contract</option>
                  <option value="internship">Internship</option>
                  <option value="remote">Remote</option>
                </select>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="form-select"
                >
                  <option value="draft">Draft</option>
                  <option value="open">Open (Visible to Candidates)</option>
                  <option value="closed">Closed</option>
                </select>
                <small className="form-hint">
                  <i className="fas fa-info-circle"></i>{' '}
                  {formData.status === 'open'
                    ? 'Candidates can view and apply to this position'
                    : formData.status === 'draft'
                    ? 'Only admins can see this requirement'
                    : 'This position is closed for applications'}
                </small>
              </div>
            </div>

            {/* EXPERIENCE */}
            <div className="form-row">
              <div className="form-group full-width">
                <label>
                  <i className="fas fa-clock"></i> Experience Required
                </label>
                <select
                  name="experience_range"
                  value={formData.experience_range}
                  onChange={handleSelectChange}
                  className="form-select experience-select"
                >
                  <option value="">Select Experience Range</option>
                  {EXPERIENCE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <small className="form-hint">
                  <i className="fas fa-info-circle"></i> Select the minimum experience
                  required for this position
                </small>
              </div>
            </div>

            <div className="form-group">
              <label>Job Description</label>
              <textarea
                name="job_description"
                value={formData.job_description || ''}
                onChange={handleInputChange}
                placeholder="Detailed description about the position..."
                className="form-textarea"
                rows="4"
              />
            </div>

            {/* CUSTOM FIELDS */}
            <div className="form-section">
              <h3>Custom Fields</h3>
              <p className="form-section-hint">
                Add custom fields to collect specific information from applicants
              </p>

              <div className="field-builder">
                <div className="field-input-row">
                  <div className="field-input-group">
                    <input
                      type="text"
                      name="field_name"
                      value={fieldInput.field_name}
                      onChange={handleFieldInputChange}
                      placeholder="Field Name (e.g., Portfolio URL)"
                      className="form-input"
                    />
                  </div>

                  <div className="field-input-group">
                    <select
                      name="field_type"
                      value={fieldInput.field_type}
                      onChange={handleFieldInputChange}
                      className="form-select"
                    >
                      {fieldTypes.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="field-input-group checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        name="is_required"
                        checked={fieldInput.is_required}
                        onChange={handleFieldInputChange}
                      />
                      Required
                    </label>
                  </div>
                </div>

                <div className="field-action">
                  <button
                    type="button"
                    onClick={handleAddField}
                    className="btn-add-field"
                  >
                    <i
                      className={`fas ${
                        editingFieldIndex !== null ? 'fa-save' : 'fa-plus'
                      }`}
                    ></i>
                    {editingFieldIndex !== null ? 'Update Field' : 'Add Field'}
                  </button>
                  {editingFieldIndex !== null && (
                    <button
                      type="button"
                      onClick={() => {
                        setEditingFieldIndex(null);
                        setFieldInput({
                          field_name: '',
                          field_type: 'text',
                          is_required: false,
                          options: [],
                          optionInput: '',
                        });
                      }}
                      className="btn-cancel-edit"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>

              {formData.fields.length > 0 && (
                <div className="fields-list">
                  {formData.fields.map((field, index) => (
                    <div key={index} className="field-item">
                      <div className="field-item-info">
                        <div className="field-item-name">
                          <i className={`fas ${getFieldTypeIcon(field.field_type)}`}></i>
                          <span>{field.field_name}</span>
                          {field.is_required && (
                            <span className="field-required-badge">Required</span>
                          )}
                          <span className="field-type-badge">
                            {getFieldTypeLabel(field.field_type)}
                          </span>
                        </div>
                      </div>
                      <div className="field-item-actions">
                        <button
                          type="button"
                          onClick={() => handleEditField(index)}
                          className="btn-field-edit"
                        >
                          <i className="fas fa-edit"></i>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveField(index)}
                          className="btn-field-delete"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={resetForm}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={submitting || !hasPermission}
              >
                <i
                  className={`fas ${
                    submitting
                      ? 'fa-spinner fa-spin'
                      : editingId
                      ? 'fa-save'
                      : 'fa-plus'
                  }`}
                ></i>
                {submitting
                  ? 'Saving...'
                  : editingId
                  ? 'Update Requirement'
                  : 'Create Requirement'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* LIST */}
      <div className="requirements-list">
        <div className="list-header">
          <h2>
            <i className="fas fa-list"></i> All Requirements ({pagination.total})
          </h2>
          {loading && (
            <span className="loading-spinner">
              <i className="fas fa-spinner fa-spin"></i>
            </span>
          )}
        </div>

        {requirements.length === 0 ? (
          <div className="empty-requirements">
            <i className="fas fa-bullhorn"></i>
            <p>{loading ? 'Loading...' : 'No requirements found'}</p>
            {!loading && (
              <span>Click "New Requirement" to create your first requirement</span>
            )}
          </div>
        ) : (
          <>
            <div className="requirements-grid">
              {requirements.map((req) => {
                // ✅ Normalize locations for display (backend gives is_open)
                let locations = req.locations || [];
                if (Array.isArray(locations)) {
                  locations = locations
                    .map((loc) => {
                      if (typeof loc === 'string') {
                        return { name: loc, is_open: true };
                      }
                      if (typeof loc === 'object' && loc !== null) {
                        return {
                          name: loc.name || loc.location || loc.city || '',
                          is_open:
                            loc.is_open !== undefined
                              ? loc.is_open !== false
                              : loc.enabled !== undefined
                              ? loc.enabled !== false
                              : true,
                        };
                      }
                      return { name: String(loc), is_open: true };
                    })
                    .filter((loc) => loc.name);
                }

                // Experience display
                let experienceDisplay = 'Not specified';
                if (req.experience_min !== null && req.experience_min !== undefined) {
                  if (req.experience_max === null) {
                    experienceDisplay = `${req.experience_min}+ years`;
                  } else {
                    experienceDisplay = `${req.experience_min} - ${req.experience_max} years`;
                  }
                }

                return (
                  <div key={req.id} className="requirement-card">
                    <div className="req-card-header">
                      <div>
                        <h3>{req.title}</h3>
                        <span className="req-code">{req.requirement_code}</span>
                      </div>
                      {getStatusBadge(req.status)}
                    </div>

                    <div className="req-card-details">
                      {req.department && (
                        <div className="req-detail-item">
                          <i className="fas fa-building"></i>
                          <span>{req.department}</span>
                        </div>
                      )}

                      {locations.length > 0 && (
                        <div className="req-detail-item locations-display">
                          <i className="fas fa-map-marker-alt"></i>
                          <div className="location-list">
                            {locations.map((loc, idx) => (
                              <span
                                key={idx}
                                className={`location-chip ${
                                  !loc.is_open ? 'chip-disabled' : ''
                                }`}
                                title={loc.is_open ? 'Active' : 'Disabled'}
                              >
                                {loc.name}
                                {!loc.is_open && (
                                  <i className="fas fa-pause-circle chip-icon"></i>
                                )}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {req.job_type && (
                        <div className="req-detail-item">
                          <i className="fas fa-briefcase"></i>
                          <span>{req.job_type}</span>
                        </div>
                      )}

                      <div className="req-detail-item">
                        <i className="fas fa-clock"></i>
                        <span>Experience: {experienceDisplay}</span>
                      </div>

                      {req.created_at && (
                        <div className="req-detail-item">
                          <i className="fas fa-calendar"></i>
                          <span>
                            Created: {new Date(req.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>

                    {req.fields && req.fields.length > 0 && (
                      <div className="req-fields-preview">
                        <span className="fields-label">Fields:</span>
                        {req.fields.slice(0, 3).map((field, idx) => (
                          <span key={idx} className="field-preview-tag">
                            <i className={`fas ${getFieldTypeIcon(field.field_type)}`}></i>
                            {field.field_name}
                            {field.is_required && <span className="required-dot">*</span>}
                          </span>
                        ))}
                        {req.fields.length > 3 && (
                          <span className="field-more">
                            +{req.fields.length - 3} more
                          </span>
                        )}
                      </div>
                    )}

                    {req.job_description && (
                      <div className="req-card-description">
                        <p>{req.job_description.substring(0, 150)}...</p>
                      </div>
                    )}

                    {/* JD Upload */}
                    <div className="req-jd-upload">
                      <input
                        type="file"
                        id={`jd-upload-${req.id}`}
                        style={{ display: 'none' }}
                        onChange={(e) => {
                          if (e.target.files[0]) {
                            handleFileUpload(req.id, e.target.files[0]);
                          }
                          e.target.value = '';
                        }}
                      />
                      <button
                        type="button"
                        className="btn-upload-jd"
                        onClick={() =>
                          document.getElementById(`jd-upload-${req.id}`).click()
                        }
                        disabled={!hasPermission}
                      >
                        <i className="fas fa-upload"></i>
                        {req.jd_file_key ? 'Update JD' : 'Upload JD'}
                      </button>
                      {req.jd_file_key && (
                        <span className="jd-status">
                          <i className="fas fa-check-circle"></i> Uploaded
                        </span>
                      )}
                    </div>

                    <div className="req-card-actions">
                      <button
                        className="btn-edit-req"
                        onClick={() => handleEdit(req.id)}
                        disabled={!hasPermission}
                      >
                        <i className="fas fa-edit"></i> Edit
                      </button>
                      <button
                        className="btn-delete-req"
                        onClick={() => handleDelete(req.id)}
                        disabled={!hasPermission}
                      >
                        <i className="fas fa-trash"></i> Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {pagination.totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="page-btn"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <span className="page-info">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  className="page-btn"
                >
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CreateRequirement;